import {ButtonHTMLAttributes, ReactElement} from "react";
import FrameButton from "@/components/buttons/FrameButton";
import {NftOwnership} from "@devour/client";
import {RewardNftRedemptionState} from "@/components/goVip/GoVipRewardsSelectedNft";
import {useActiveWalletConnectionStatus} from "thirdweb/react";
import {toggleReauthenticateModal} from "@/redux/meta/metaActions";
import {useDispatch, useSelector} from "react-redux";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {IStore} from "@/redux/defaultStore";
import type {Chain} from "thirdweb/chains";
import RedeemLootboxButton from "@/components/RedeemLootboxButton";

interface Props {
    redemptionState: RewardNftRedemptionState;
    toggleDrawer: () => void;
    ownership: NftOwnership;
    onVirtualRedemption: () => void;
    contractAddress: string;
    onTransactionStarted?: () => void;
    onTransactionSubmit: (transactionHash: `0x${string}`) => void;
    onTransactionConfirmed: (transactionHash: `0x${string}`) => void;
    relayerUrl?: string;
    forwarderAddress?: string;
    chain?: Chain;
}

export default function GoVipRewardsSelectedXpMultiplierFooter(props: Props): ReactElement {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const thirdwebStatus = useActiveWalletConnectionStatus();
    const {data: userProfileData} = useGetUserProfile(fullToken);
    const xpMultipliers = userProfileData?.user?.xpMultipliers?.filter((doc) => {
        // Check that the multiplier has not expired.
        return doc.expiresAt >= Date.now();
    });
    const multiplerAmount = xpMultipliers?.reduce((accumulated, doc) => {
        const boost = doc.amount - 1;
        return accumulated + boost;
    }, 1);

    function onRedeemNow() {
        if (props.ownership.isVirtual) {
            props.onVirtualRedemption();
            return;
        }
        if (thirdwebStatus !== "connected") {
            dispatch(toggleReauthenticateModal(true));
        }
    }

    function renderDefaultFooter(): ReactElement {
        const warning = multiplerAmount > 1 &&
            <div className="govip-rewards-drawer_footer_danger">
                You currently have an active XP multiplier.<br/>
                Multipliers do not stack.<br/>
                Redeeming this will replace your existing XP multiplier.
            </div>;

        if (thirdwebStatus !== "connected" || props.ownership.isVirtual) {
            return (
                <div className="govip-rewards-drawer_footer_default">
                    {warning}
                    <FrameButton
                        color="white-drop-shadow-devour"
                        size="normal"
                        className="govip-rewards-drawer_footer_default_button"
                        onClick={onRedeemNow}
                    >
                        Redeem Now
                    </FrameButton>
                </div>
            );
        }

        return (
            <div className="govip-rewards-drawer_footer_default">
                {warning}
                <RedeemLootboxButton
                    nftId={props.ownership.nftId}
                    contractAddress={props.contractAddress}
                    onTransactionStarted={props.onTransactionStarted}
                    onTransactionSubmit={props.onTransactionSubmit}
                    onTransactionConfirmed={props.onTransactionConfirmed}
                    relayerUrl={props.relayerUrl}
                    forwarderAddress={props.forwarderAddress}
                    chain={props.chain}
                />
            </div>
        );
    }

    function renderRedemptionOpeningFooter(): ReactElement {
        return (
            <div className="govip-rewards-drawer_footer_in-progress">
                Reward attempt was disconnected.
                Please wait a minute and try again.
            </div>
        );
    }

    function renderRedemptionInProgressFooter(): ReactElement {
        return (
            <div className="govip-rewards-drawer_footer_in-progress">
                Unlocking
                <div className="loading-dot" />
                <div className="loading-dot" />
                <div className="loading-dot" />
            </div>
        );
    }

    function renderPrizeRevealFooter(): ReactElement {
        return (
            <div className="govip-rewards-drawer_footer_default">
                <FrameButton
                    <ButtonHTMLAttributes<HTMLButtonElement>>
                    color="purple-outline"
                    size="normal"
                    className="govip-rewards-drawer_footer_default_button"
                    onClick={props.toggleDrawer}
                >
                    Confirm and close
                </FrameButton>
            </div>
        );
    }

    return (
        <>
            {props.redemptionState === RewardNftRedemptionState.NOT_INITIATED && renderDefaultFooter()}
            {props.redemptionState === RewardNftRedemptionState.OPENING && renderRedemptionOpeningFooter()}
            {props.redemptionState === RewardNftRedemptionState.IN_PROGRESS && renderRedemptionInProgressFooter()}
            {props.redemptionState === RewardNftRedemptionState.DONE && renderPrizeRevealFooter()}
        </>
    );

}